import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signInWithCredential,
  GoogleAuthProvider,
  signOut, 
  onAuthStateChanged,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  multiFactor,
  RecaptchaVerifier
} from 'firebase/auth';
import { doc, getDoc, getDocs, setDoc, updateDoc, collection, addDoc, onSnapshot, query, where } from 'firebase/firestore';
import { auth, googleProvider, db } from '../services/firebase';
import { trackEvent } from '../services/analytics';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userLicense, setUserLicense] = useState(null);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribeLicense = null;

    // Handle Tauri's deep link result
    if (window.__IS_DESKTOP__) {
      import('@tauri-apps/plugin-deep-link').then(({ onOpenUrl }) => {
        onOpenUrl(async (urls) => {
          for (const url of urls) {
            if (url.includes('snipit://auth')) {
              try {
                const token = new URL(url).searchParams.get('token');
                if (token) {
                  const credential = GoogleAuthProvider.credential(token);
                  await signInWithCredential(auth, credential);
                }
              } catch (e) {
                console.error("Deep link auth error:", e);
              }
            }
          }
        }).catch((err) => console.error("Deep link register error:", err));
      }).catch((err) => console.error("Deep link plugin load error:", err));
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // --- STRIPE SUBSCRIPTION VERIFICATION ---
        const subsRef = collection(db, 'customers', user.uid, 'subscriptions');
        const q = query(subsRef, where('status', 'in', ['trialing', 'active', 'past_due']));
        
        unsubscribeLicense = onSnapshot(q, async (snap) => {
          let currentLicense = { tier: 'free', status: 'active', features: { cloudSync: false, aiVault: false, teamSharing: false } };
          let actualRole = 'free';
          let subStatus = 'active';
          
          if (!snap.empty) {
            const subData = snap.docs[0].data();
            subStatus = subData.status;
            
            // 1. Check if role is directly on the subscription metadata
            actualRole = subData.role || subData.metadata?.firebaseRole || 'free';
            
            // 2. If still free, check the linked product document
            if (actualRole === 'free') {
              const productId = subData.items?.[0]?.price?.product || subData.items?.[0]?.plan?.product;
              if (typeof productId === 'string') {
                try {
                  const productRef = doc(db, 'products', productId);
                  const productSnap = await getDoc(productRef);
                  if (productSnap.exists()) {
                    actualRole = productSnap.data().role || productSnap.data().metadata?.firebaseRole || 'premium';
                  } else {
                    // Fallback to premium if they have an active sub but product sync failed
                    actualRole = 'premium';
                  }
                } catch (e) {
                  console.error("Failed to fetch product role:", e);
                  actualRole = 'premium';
                }
              } else if (subData.items?.length > 0) {
                actualRole = 'premium';
              }
            }
          }
          
          // Fallback 1: Check Custom Claims (Firebase Stripe Extension often uses this)
          if (actualRole === 'free') {
            try {
              const token = await user.getIdTokenResult();
              if (token.claims.stripeRole) {
                actualRole = token.claims.stripeRole;
              }
            } catch (e) { console.error(e); }
          }
          
          // Fallback 2: Check Organization Membership (For Enterprise Teams users who didn't buy it themselves)
          if (actualRole === 'free') {
            try {
              const orgRef = collection(db, 'org_members');
              const orgQ = query(orgRef, where('userId', '==', user.uid));
              const orgSnap = await getDocs(orgQ);
              if (!orgSnap.empty) {
                actualRole = 'enterprise_org';
              }
            } catch (e) { console.error(e); }
          }

          // Fallback 3: Web Store Reviewer Whitelist
          if (actualRole === 'free' && user.email && (user.email.endsWith('@google.com') || user.email.endsWith('@chromium.org') || user.email === 'jeremydtyler86@gmail.com')) {
            actualRole = 'premium';
            subStatus = 'active';
          }

          currentLicense = {
            tier: actualRole,
            status: subStatus,
            features: {
              cloudSync: actualRole === 'premium' || actualRole === 'enterprise' || actualRole === 'enterprise_org',
              aiVault: actualRole === 'premium' || actualRole === 'enterprise' || actualRole === 'enterprise_org',
              teamSharing: actualRole === 'enterprise_org'
            }
          };
          
          setUserLicense(currentLicense);
        });

        // Fetch or create user profile in Firestore
        try {
          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            // Create new user profile with starting DevIT Creds
            const newProfile = {
              uid: user.uid,
              displayName: user.displayName,
              email: user.email,
              photoURL: user.photoURL,
              devItCreds: 0,
              createdAt: new Date().toISOString()
            };
            await setDoc(userRef, newProfile);
            setUserProfile(newProfile);
          } else {
            setUserProfile(userSnap.data());
          }
        } catch (error) {
          console.error("Failed to fetch user profile, likely due to expired token or network drop:", error);
        }
      } else {
        setUserProfile(null);
        setUserLicense(null);
        if (unsubscribeLicense) {
          unsubscribeLicense();
        }
      }
      setLoading(false);
    });

    return () => {
      if (unsubscribeLicense) {
        unsubscribeLicense();
      }
      unsubscribe();
    };
  }, []);

  const loginWithGoogle = async () => {
    try {
      if (typeof chrome !== 'undefined' && chrome.identity) {
        return new Promise((resolve, reject) => {
          chrome.identity.getAuthToken({ interactive: true }, async (token) => {
            if (chrome.runtime.lastError || !token) {
              console.error("Chrome Identity Error:", chrome.runtime.lastError);
              reject(chrome.runtime.lastError);
              return;
            }
            try {
              const credential = GoogleAuthProvider.credential(null, token);
              await signInWithCredential(auth, credential);
              trackEvent('login', { platform: 'chrome_extension' });
              resolve();
            } catch (err) {
              reject(err);
            }
          });
        });
      } else if (window.__IS_DESKTOP__) {
        // Tauri desktop deep link flow: Open system browser to our secure web portal
        // This will open a popup window for web, or the system browser for Tauri
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        const authUrl = import.meta.env.VITE_WEB_URL || (import.meta.env.DEV ? 'http://localhost:5173' : 'https://gen-lang-client-0279358733.web.app');
        await openUrl(`${authUrl}/desktop-auth`);
      } else {
        await signInWithPopup(auth, googleProvider);
        trackEvent('login', { platform: 'web' });
      }
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, error);
        setMfaResolver(resolver);
        throw error;
      } else if (error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-closed-by-user') {
        console.log("User cancelled the sign-in popup.");
        return null; // Return null instead of throwing to prevent unhandled rejections
      } else {
        console.error("Login failed", error);
        throw error;
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      trackEvent('logout');
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  const updateProfile = async (updates) => {
    if (!currentUser) return;
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, updates);
      setUserProfile(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error("Failed to update profile", error);
      throw error;
    }
  };

  // --- MFA Methods ---

  const sendMfaSignInCode = async (recaptchaVerifier) => {
    if (!mfaResolver) throw new Error("No MFA resolver found.");
    
    // We assume the first hint is the phone number (or later, we can support TOTP)
    const phoneHint = mfaResolver.hints.find(h => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
    if (!phoneHint) throw new Error("No phone factor enrolled.");

    const phoneInfoOptions = {
      multiFactorHint: phoneHint,
      session: mfaResolver.session
    };
    
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    return await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
  };

  const completeMfaSignIn = async (verificationId, verificationCode) => {
    if (!mfaResolver) throw new Error("No MFA resolver found.");
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await mfaResolver.resolveSignIn(multiFactorAssertion);
    setMfaResolver(null); // Clear resolver on success
  };

  const startMfaEnrollment = async (phoneNumber, recaptchaVerifier) => {
    if (!currentUser) throw new Error("User not logged in.");
    const session = await multiFactor(currentUser).getSession();
    const phoneInfoOptions = { phoneNumber, session };
    const phoneAuthProvider = new PhoneAuthProvider(auth);
    return await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);
  };

  const completeMfaEnrollment = async (verificationId, verificationCode, displayName = "Primary Phone") => {
    if (!currentUser) throw new Error("User not logged in.");
    const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
    const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactor(currentUser).enroll(multiFactorAssertion, displayName);
  };

  const clearMfa = () => {
    setMfaResolver(null);
  };

  const value = {
    currentUser,
    userProfile,
    userLicense,
    mfaResolver,
    loginWithGoogle,
    logout,
    updateProfile,
    sendMfaSignInCode,
    completeMfaSignIn,
    startMfaEnrollment,
    completeMfaEnrollment,
    clearMfa
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
